import { useRootAppStore } from "@hooks";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { history, SessionStorageProvider } from "@state";
import { DeviceDetector, getCurrentCulture } from "@utils";
import { isCapacitorPlatform } from "@utils/specific/capacitor";

const MAX_EVENTS = 10;

function parseTipString(tipStr: string) {
	const decodedTip = decodeURIComponent(tipStr);
	const parts = decodedTip.split("|").filter(Boolean);

	const eventId = parts[0];
	const tipId = parts[1];
	const isBetBuilder = parts.includes("bb");
	const isBank = parts.includes("bank");

	const multiParts = parts.filter(
		(p) => p !== eventId && p !== tipId && p !== "bb" && p !== "bank"
	);

	let multiOffers: { multiOfferId: string; value: number }[] | undefined;
	let value: number | undefined;

	if (isBetBuilder) {
		const multiOfferParts = multiParts.filter((p) => p.includes("-"));
		if (multiOfferParts.length > 0) {
			multiOffers = multiOfferParts.map((p) => {
				const [id, val] = p.split("-");
				return { multiOfferId: id, value: Number(val) };
			});
		}

		const lastPart = multiParts[multiParts.length - 1];

		if (!lastPart.includes("-")) {
			value = Number(lastPart);
		}
	} else {
		if (multiParts.length > 0) {
			value = Number(multiParts[multiParts.length - 1]);
		}
	}

	return { eventId, tipId, isBetBuilder, multiOffers, isBank, value };
}

export function toFixedMoney(value: string): number {
	return Number(Number(value).toFixed(2));
}

export function useAddTipsFromUrl() {
	const rootStore = useRootAppStore();
	const location = useLocation();
	const processedTipKey = useRef<string | null>(null);
	const unlistenRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		return () => {
			unlistenRef.current?.();
		};
	}, []);

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);

		const tipsRaw = urlParams.getAll("tip").slice(0, MAX_EVENTS);

		if (!tipsRaw.length) {
			return;
		}

		// Prevent re-processing the same tips when other query params change
		// while tip params are still present in the URL (e.g. mobile web keeps
		// them so the native app can read them on "Open in App").
		const tipKey = tipsRaw.join(",");
		if (processedTipKey.current === tipKey) {
			return;
		}
		processedTipKey.current = tipKey;

		if (rootStore.betSlipStore.betSlipState.slip) {
			rootStore.betSlipStore.onResetSlip();
			SessionStorageProvider.remove("slip");
		}

		const parsedTips = tipsRaw.map(parseTipString);

		const getUrlParams = () => {
			const betSlipType = urlParams.get("type") as string | null;
			const system = urlParams.get("system") as string | null;

			return {
				betSlipType,
				system,
			};
		};

		const removeUrlParams = () => {
			urlParams.delete("tip");
			urlParams.delete("type");
			urlParams.delete("system");
		};

		const addTipsToSlip = async () => {
			const bankEventIdsFromUrl = parsedTips
				.filter((tip) => tip.isBank)
				.map((tip) => tip.eventId);

			rootStore.betSlipStore.betSlipState.bankEvents = [
				...new Set([...bankEventIdsFromUrl]),
			];

			const { system, betSlipType } = getUrlParams();

			if (system) {
				const systemArray = system
					.split("+")
					.map((s) => Number(s))
					.filter((n) => !isNaN(n));

				if (systemArray.length !== 0) {
					SessionStorageProvider.set(
						"ssystem",
						JSON.stringify(systemArray)
					);

					rootStore.betSlipStore.betSlipState.selectedCombinations =
						systemArray;
				}
			}

			for (const tip of parsedTips) {
				if (!tip.tipId) {
					continue;
				}

				if (rootStore.betSlipStore.isTipSelected(tip.tipId)) {
					continue;
				}

				await rootStore.betSlipStore.addRemoveOffer({
					id: tip.tipId,
					eventId: tip.eventId,
					isBank: tip.isBank,
					value: tip.value,
					system: system ? system : null,
					multiOffers: tip.isBetBuilder ? tip.multiOffers : undefined,
				});
			}

			// On mobile web, keep tip params in the URL so that if the user
			// taps "Open in App", the native app receives the full URL with
			// tip params and can process them. On Capacitor, strip them since
			// the app has already processed them.
			if (isCapacitorPlatform() || DeviceDetector.isDesktopTheme) {
				removeUrlParams();
			}

			const newSearch = urlParams.toString();

			const culture = getCurrentCulture();

			const modifiedPathname =
				DeviceDetector.isMobileTheme || isCapacitorPlatform()
					? `/${culture}/bet-slip`
					: "";

			const newUrl =
				modifiedPathname + (newSearch ? `?${newSearch}` : "");

			history.replace(newUrl);

			// On mobile web, register a one-time listener to strip the bet slip
			// params on the next navigation (after "Open in App" has had a chance
			// to read them from the URL).
			if (!isCapacitorPlatform()) {
				unlistenRef.current?.();
				unlistenRef.current = history.listen((nextLocation) => {
					unlistenRef.current?.();
					unlistenRef.current = null;

					const nextParams = new URLSearchParams(nextLocation.search);
					const nextTipKey = nextParams
						.getAll("tip")
						.slice(0, MAX_EVENTS)
						.join(",");

					// If the next URL has different tip params, a new shared
					// link was opened — skip cleaning and let the hook process them.
					if (nextTipKey !== tipKey) {
						return;
					}

					nextParams.delete("tip");
					nextParams.delete("type");
					nextParams.delete("system");

					const cleanSearch = nextParams.toString();
					history.replace(
						nextLocation.pathname +
							(cleanSearch ? `?${cleanSearch}` : "")
					);
				});
			}

			if (betSlipType && JSON.parse(betSlipType) == "system") {
				SessionStorageProvider.set("type", "system");
				rootStore.betSlipStore.onBetSlipTypeChange("system");
			}
		};

		addTipsToSlip();
	}, [location.search]);
}
